import React, { useState } from "react";
import { Link } from "react-router-dom";
import { loadRun, saveRun, resetRun, RunState, PHASES, TOTAL_STEPS } from "../lib/runState";
import UpdateLayerCard from "../components/UpdateLayerCard";
import DiseaseMapGenerationCard from "../components/DiseaseMapGenerationCard";
import SprayNotificationsCard from "../components/SprayNotificationsCard";
import MarkTreatedPanel from "../components/run/MarkTreatedPanel";
import ClearTreatmentPanel from "../components/run/ClearTreatmentPanel";

function GateStep({ n, label, done, enabled, onDo, doneAt }: { n: number; label: string; done: boolean; enabled: boolean; onDo: () => void; doneAt?: string | null }) {
  return (
    <div className={`flex items-center gap-3 border border-line rounded-sm2 px-3 py-2.5 bg-card ${!enabled && !done ? "opacity-50" : ""}`}>
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-brand text-white" : "bg-paper-2 text-ink-soft"}`}>
        {done ? "✓" : n}
      </span>
      <span className="font-semibold text-sm flex-1">{label}</span>
      {done ? (
        <span className="font-mono text-xs text-ink-faint">{doneAt ? new Date(doneAt).toLocaleString() : "done"}</span>
      ) : (
        <button className="btn-primary !py-1 !px-3 text-xs" disabled={!enabled} onClick={onDo}>Mark done</button>
      )}
    </div>
  );
}

export default function RunPage() {
  const [run, setRun] = useState<RunState>(loadRun);
  const [expanded, setExpanded] = useState<number | null>(null);

  const update = (patch: Partial<RunState>) => {
    const next = { ...run, ...patch };
    saveRun(next);
    setRun(next);
  };

  const completeStep = (n: number) => {
    update({
      step: n + 1,
      stepCompletedAt: { ...run.stepCompletedAt, [n]: new Date().toISOString() },
    });
    setExpanded(null);
  };

  const complete = run.step > TOTAL_STEPS;
  const pct = Math.min(((run.step - 1) / TOTAL_STEPS) * 100, 100);

  const panels: Record<number, React.ReactElement> = {
    1: (
      <div className="space-y-4">
        <p className="text-ink-soft text-sm">
          Download the two <b>VectorSurv legacy CSV exports</b> (Arthropod Collections + Arthropod Pools) — they carry exact
          trap Lat/Lon that the VectorSurv API omits. Upload each below to refresh the dashboards.
        </p>
        <UpdateLayerCard layerType="surveillance" />
        <UpdateLayerCard layerType="disease" />
      </div>
    ),
    2: (
      <div className="space-y-4">
        <p className="text-ink-soft text-sm">
          Analyze the latest week's positives. Results carry forward to the notification list and the Treatment Builder.
        </p>
        <DiseaseMapGenerationCard />
      </div>
    ),
    3: (
      <div className="space-y-3">
        <p className="text-ink-soft text-sm">
          Pick the subgrids containing the positives, adjust the boundary if needed, compose the Community Notice, and
          save each area. When the week is ready, <b>Schedule for treatment</b> pushes them all to the AGOL layer that
          the webmap and website already read — no ArcGIS Pro detour.
        </p>
        <p className="text-ink-soft text-sm">
          Do this <b>before</b> pulling the notification list: the notification subgrids come from the areas you actually
          scheduled, which is not always the same as where the positives were.
        </p>
        <Link to="/builder" className="btn-primary inline-block">Open Treatment Builder</Link>
      </div>
    ),
    4: (
      <div className="space-y-4">
        <p className="text-ink-soft text-sm">
          Generate the resident notification list CSV and send it to Kathy. Fill the subgrids straight from the areas
          scheduled in step 3 rather than retyping them.
        </p>
        <SprayNotificationsCard />
      </div>
    ),
    5: (
      <div className="space-y-3">
        <div className="border-l-4 border-wnv bg-wnv-tint rounded-sm2 px-4 py-3 text-sm">
          <b className="text-wnv">Coupled gate:</b> the website must go live <b>before</b> notices are sent, but{" "}
          <b>only if notices go out the same day</b>. If Kathy can't send today, the website stays down — public
          treatment info must never post ahead of resident notice.
        </div>
        <label className="flex items-center gap-2.5 font-semibold text-sm">
          <input
            type="checkbox"
            checked={run.gateKathyConfirmed}
            onChange={(e) => update({ gateKathyConfirmed: e.target.checked })}
            className="w-4 h-4 accent-[#5B9E3F]"
          />
          Notices will be sent today (Kathy confirmed)
        </label>
        <GateStep
          n={1}
          label="Set website text + map live"
          done={!!run.gateWebsiteLiveAt}
          enabled={run.gateKathyConfirmed}
          doneAt={run.gateWebsiteLiveAt}
          onDo={() => update({ gateWebsiteLiveAt: new Date().toISOString() })}
        />
        <GateStep
          n={2}
          label="Send notices + community maps (Kathy → residents; Annie + law enforcement → maps)"
          done={!!run.gateNoticesSentAt}
          enabled={run.gateKathyConfirmed && !!run.gateWebsiteLiveAt}
          doneAt={run.gateNoticesSentAt}
          onDo={() => update({ gateNoticesSentAt: new Date().toISOString() })}
        />
      </div>
    ),
    6: (
      <div className="space-y-3">
        <p className="text-ink-soft text-sm">
          Export spray routes (SHP) → FieldSeeker ULV Office → field techs sync Mesa devices. Route drawing stays in
          ArcGIS Pro for now. This step is skippable.
        </p>
        <label className="flex items-center gap-2.5 font-semibold text-sm">
          <input type="checkbox" className="w-4 h-4 accent-[#5B9E3F]" /> ULV devices charged &amp; ready, Thu 3:00 PM
        </label>
      </div>
    ),
    7: (
      <div className="space-y-4">
        <p className="text-ink-soft text-sm">
          Revert the website to "No treatment scheduled", record what was treated, then take this week's areas off the
          public map.
        </p>
        <div className="border-l-4 border-wnv bg-wnv-tint rounded-sm2 px-4 py-3 text-sm">
          <b className="text-wnv">Do these in order.</b> <b>Fill from this week's scheduled areas</b> reads the areas
          still marked <span className="font-mono text-xs">Upcoming</span>. Clearing the schedule first flips them to{" "}
          <span className="font-mono text-xs">Completed</span>, and the subgrid list would come back empty.
        </div>

        <div className="space-y-2">
          <div className="font-extrabold text-[13.5px]">1 · Mark the treated subgrids</div>
          <p className="text-ink-soft text-sm">
            Use <b>Fill from this week's scheduled areas</b> instead of retyping the labels — it also sets the date from
            the scheduled areas, so the record carries the treatment date rather than today's.
          </p>
          <MarkTreatedPanel />
        </div>

        <div className="space-y-2 pt-1">
          <div className="font-extrabold text-[13.5px]">2 · Clear the treatment schedule</div>
          <p className="text-ink-soft text-sm">
            Flips this week's areas to <span className="font-mono text-xs">Completed</span> so the website and webmap
            stop showing them. The polygons stay on the layer as history.
          </p>
          <ClearTreatmentPanel />
        </div>
      </div>
    ),
  };

  // The go-live gate (now step 5) requires notices confirmed sent before continuing.
  const canContinue = (n: number) => (n === 5 ? !!run.gateNoticesSentAt : true);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Link to="/workflows" className="text-brand-deep font-semibold">← Workflows</Link>
        <button
          className="btn-ghost !py-1 !px-3 text-xs"
          onClick={() => {
            if (window.confirm("Reset the run to step 1?")) setRun(resetRun());
          }}
        >
          Reset run
        </button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="pill bg-wnv-tint text-wnv">Active response</span>
          <h2 className="font-extrabold text-2xl tracking-tight mt-1.5">Adult Control Run</h2>
          <p className="text-ink-soft text-sm mt-0.5">
            Weekly response · deadline <span className="text-wnv font-semibold">ULV Thu 3:00 PM</span>
          </p>
        </div>
        <div className="ui-card px-4 py-3 min-w-[220px]">
          <div className="flex justify-between text-xs font-mono mb-1.5">
            <span>Progress</span>
            <span>{complete ? "Complete" : `Phase ${run.step} / ${TOTAL_STEPS}`}</span>
          </div>
          <div className="h-2 bg-paper-2 rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {complete && (
        <div className="ui-card border-l-4 !border-l-brand p-4 mb-5 font-semibold text-brand-deep">
          ✓ Run complete — all {TOTAL_STEPS} phases done. Reset to start next week's response.
        </div>
      )}

      <div className="space-y-2.5">
        {PHASES.map((phase) => {
          const status = phase.num < run.step ? "done" : phase.num === run.step ? "active" : "locked";
          const open = status === "active" || expanded === phase.num;
          return (
            <div key={phase.num} className={`ui-card overflow-hidden ${status === "locked" ? "opacity-55" : ""}`}>
              <button
                className="w-full flex items-center gap-3.5 px-4 py-3.5 text-left disabled:cursor-not-allowed"
                disabled={status === "locked"}
                onClick={() => status === "done" && setExpanded(expanded === phase.num ? null : phase.num)}
              >
                <span
                  className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                    status === "done"
                      ? "bg-brand text-white"
                      : status === "active"
                      ? "border-2 border-brand text-brand-deep"
                      : "bg-paper-2 text-ink-faint"
                  }`}
                >
                  {status === "done" ? "✓" : phase.num}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="font-extrabold text-[15.5px] block">{phase.title}</span>
                  <span className="text-ink-faint text-xs font-mono">{phase.tool}</span>
                </span>
                <span
                  className={`pill ${
                    status === "done"
                      ? "bg-brand-tint text-brand-deep"
                      : status === "active"
                      ? "bg-info-tint text-info"
                      : "bg-paper-2 text-ink-faint"
                  }`}
                >
                  {status === "done" ? "Done" : status === "active" ? "In progress" : "🔒 Locked"}
                </span>
              </button>
              {open && status !== "locked" && (
                <div className="border-t border-line px-4 py-4 bg-paper">
                  {panels[phase.num]}
                  {status === "active" && (
                    <button className="btn-primary mt-4" disabled={!canContinue(phase.num)} onClick={() => completeStep(phase.num)}>
                      {phase.num === TOTAL_STEPS ? "Complete run" : "Mark step complete & continue"}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
